import { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import {
	LOAN_PURPOSE_BUSINESS,
	LOAN_PURPOSE_PERSONAL,
	ABN_STATUS_NEW_ABN,
	EMP_STAT_SELF_EMPLOYED,
	LOAN_PURPOSE_EQUIPMENT,
	LOAN_PURPOSE_NOVATED_LEASE,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";

export const AdditionalInfo = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const { setFormErrors, enabledFields, setValidateFormDetails, combinedIncomeMin } =
		useFormStateStore();

	const {
		loanPurpose,
		loanType,
		employmentStatus,
		abnStatus,
		gstStatus,
		creditHistory,
		combinedIncome,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		employmentStatus: false,
		abnStatus: false,
		gstStatus: false,
		creditHistory: false,
		combinedIncome: false,
	});

	const handleCombinedIncomeChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("combinedIncome", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				loanAmount: formattedValue < combinedIncomeMin,
			}));
		},
		[setFormValue, setFormErrors, setErrors],
	);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			employmentStatus:
				enabledFields.employmentStatus?.length == 0 || loanPurpose === LOAN_PURPOSE_BUSINESS
					? false
					: !employmentStatus,
			abnStatus:
				enabledFields.abnStatus?.length == 0 ||
				loanPurpose === LOAN_PURPOSE_PERSONAL ||
				loanPurpose === LOAN_PURPOSE_NOVATED_LEASE ||
				loanType === "Mortgage Loan"
					? false
					: !abnStatus,
			gstStatus:
				enabledFields.gstStatus?.length == 0 ||
				abnStatus === ABN_STATUS_NEW_ABN ||
				loanPurpose === LOAN_PURPOSE_PERSONAL ||
				loanPurpose === LOAN_PURPOSE_NOVATED_LEASE ||
				loanType === "Mortgage Loan"
					? false
					: !gstStatus,
			creditHistory: enabledFields.creditHistory?.length == 0 ? false : !creditHistory,
			combinedIncome:
				!combinedIncome || parseFloat(combinedIncome.replace(/,/g, "")) < combinedIncomeMin,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm's Next Button can trigger this.
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [employmentStatus, abnStatus, gstStatus, creditHistory, combinedIncome]);

	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	return (
		<div className="yF-space-y-4">
			{enabledFields.abnStatus?.length != 0 &&
				[LOAN_PURPOSE_BUSINESS, LOAN_PURPOSE_EQUIPMENT].includes(
					formValues.loanPurpose,
				) && (
					<>
						<RadioGroupField
							id="abnStatus"
							label="How long has your ABN been running?"
							value={abnStatus || ""}
							onChange={(value) => {
								setFormValue("abnStatus", value);
								setFormValue("employmentStatus", EMP_STAT_SELF_EMPLOYED);
								setErrors((prev) => ({
									...prev,
									abnStatus: !value,
									gstStatus:
										value === ABN_STATUS_NEW_ABN ? false : prev.gstStatus,
								}));
								if (value === ABN_STATUS_NEW_ABN) {
									setFormValue("gstStatus", "");
								}
							}}
							required
							error={errors.abnStatus ? "This field is required" : ""}
							gridCols={3}
							showIcons={false}
							className="yF-mt-4 yF-justify-center yF-w-full"
							options={enabledFields.abnStatus
								.filter((s) => s.value)
								.map((s) => ({
									id: s.value,
									value: s.value,
									label: s.option,
								}))}
						/>

						{enabledFields.gstStatus?.length != 0 &&
							formValues.abnStatus !== ABN_STATUS_NEW_ABN && (
								<RadioGroupField
									id="gstStatus"
									label="How long have you been registered for GST?"
									value={gstStatus || ""}
									onChange={(value) => {
										setFormValue("gstStatus", value);
										setErrors((prev) => ({
											...prev,
											gstStatus: !value,
										}));
									}}
									required
									showIcons={false}
									error={errors.gstStatus ? "This field is required" : ""}
									gridCols={3}
									className="yF-mt-4 yF-justify-center yF-w-full"
									options={enabledFields.gstStatus
										.filter((s) => s.value)
										.map((s) => ({
											id: s.value,
											value: s.value,
											label: s.option,
										}))}
								/>
							)}
					</>
				)}

			{enabledFields.employmentStatus?.length != 0 &&
				formValues.loanPurpose !== LOAN_PURPOSE_BUSINESS && (
					<RadioGroupField
						id="employmentStatus"
						label="What is your current employment status?"
						value={employmentStatus || ""}
						onChange={(value) => {
							setFormValue("employmentStatus", value);
							setErrors((prev) => ({ ...prev, employmentStatus: !value }));
						}}
						required
						error={errors.employmentStatus ? "This field is required" : ""}
						gridCols={3}
						showIcons={false}
						className="yF-mt-4 yF-justify-center yF-w-full"
						options={enabledFields.employmentStatus
							.filter((s) => s.value)
							.map((s) => ({
								id: s.value,
								value: s.value,
								label: s.option,
							}))}
					/>
				)}

			{enabledFields.creditHistory?.length != 0 && (
				<RadioGroupField
					id="creditHistory"
					label="How would you rate your credit history?"
					value={creditHistory || ""}
					onChange={(value) => {
						setFormValue("creditHistory", value);
						setErrors((prev) => ({
							...prev,
							creditHistory: !value,
						}));
					}}
					required
					error={errors.creditHistory ? "This field is required" : ""}
					gridCols={3}
					showIcons={false}
					className="yF-mt-4 yF-justify-center yF-w-full"
					options={enabledFields.creditHistory
						.filter((s) => s.value)
						.map((s) => ({
							id: s.value,
							value: s.value,
							label: s.option,
						}))}
				/>
			)}
			{
				<div>
					<label
						htmlFor="grossIncome"
						className="yF-block yF-text-md yF-font-semibold yF-leading-6 yF-text-gray-900"
					>
						What is your combined yearly income?
					</label>
					<div className="yF-relative yF-mt-2 yF-rounded-md yF-shadow-sm">
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
							<span className="yF-text-gray-500 sm:yF-text-md">$</span>
						</div>
						<input
							value={combinedIncome}
							id="grossIncome"
							aria-describedby="combinedIncome"
							inputMode="numeric"
							aria-label="Combined Income"
							maxLength={12}
							onChange={handleCombinedIncomeChange}
							onFocus={() => handleFocus("combinedIncome")}
							className={clsx(
								errors.combinedIncome
									? "yF-ring-red-500 focus:yF-ring-red-500"
									: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
								"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-pl-7 yF-pr-12 yF-text-gray-900 yF-ring-1 yF-ring-gray-300 focus:yF-ring-1 yF-outline-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
							)}
						/>
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-right-0 yF-flex yF-items-center yF-pr-3">
							<span className="yF-text-gray-500 sm:yF-text-sm">AUD</span>
						</div>
					</div>
					{errors.combinedIncome && (
						<span className="yF-text-xs yF-text-red-500">
							Minimum combined yearly income is ${combinedIncomeMin.toLocaleString()}
						</span>
					)}
				</div>
			}
		</div>
	);
};
